import { Button } from 'antd';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, ReactElement, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  arrowRightConsultationButton,
  arrowRightConsultationOddButton,
  certifiedIngredients,
  clarityInvoice,
  clarityProcess,
  clearlyOriginal,
  convenienceUsp,
  dropdownIcon,
  onTime,
  quicklyDelivery,
  slide1a,
  slide1b,
  slide1c,
  slide2a,
  slide2b,
  slide3a,
  slide4a,
  slide4b,
  smartConvenience,
} from '../Images';
import messages from '../messages';
import ContactUsModal from './ContacUsModal';
import { SectionContainer as CommonSectionContainer, SectionTitle, SectionTitleBox } from './Partners';
import { EventName, sendGA4Event } from '../../../../utils/triggerGA4Event';

// --- Styled Components ---

const Container = styled.div`
  background: #ffffff;
`;

const BenefitsWrapper = styled.div`
  position: relative;
  background: ${({ even }) =>
    even
      ? 'linear-gradient(180deg, rgba(255, 254, 249, 0.00) -5.78%, #FFF7E2 72.08%)'
      : 'linear-gradient(180deg, rgba(255, 254, 249, 0.00) -5.78%, #F8FFE9 72.08%)'};
`;

const SwiperElementContainer = styled.div`
  max-width: 560px;
  overflow: hidden;
  position: relative;
`;

const SwiperElement = styled(Swiper)`
  .swiper-button-prev.swiper-button-disabled {
    display: none !important;
  }
  .swiper-button-prev.swiper-button-disabled {
    display: none !important;
  }
  .swiper-button-next,
  .swiper-button-prev {
    display: none;
    &.swiper-button-disabled {
      display: none !important;
    }
  }
`;

const SwiperSlideElement = styled(SwiperSlide)`
  width: 560px !important;
  position: relative;
  border-radius: 8px;
  margin-right: 0px !important;
`;

const SectionContainer = styled.div`
  top: 0;
  display: flex;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding-bottom: 40px;
  height: 720px;
`;

const SectionWrapper = styled.div``;

const SectionItem = styled(motion.div)<{ even: boolean }>`
  width: 100%;
  max-width: 598px;
  border-radius: ${({ active }) => (active ? '12px' : '0px')};
  cursor: pointer;
  position: relative;
  background: ${({ even, active }) => (active ? (even ? '#FFEEC3' : '#E8F7C6') : 'unset')};
  padding: ${({ active }) => (active ? '32px' : '24px 32px')};
  &:not(:last-child) {
    border-bottom: ${({ active }) => (active ? 'none' : '1px solid #e5dcc7')};
  }
  ${({ notShowBorderBottom }) =>
    notShowBorderBottom &&
    `
    border-bottom: none !important;
  `}
  &:first-child {
    padding-top: ${({ active }) => (active ? '32px' : '2px')} !important;
  }
`;

const SectionItemTitle = styled.div`
  font-size: 24px;
  font-weight: 500;
  line-height: 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionItemDescription = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #343538;
`;

const SVGIconImage = styled(SVGIcon)``;

const SwiperSlideImageWrapper = styled.div`
  position: relative;
  display: flex;
  overflow: hidden;
  ${({ image }) => image && `background: url(${image});`}
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  padding-top: 100%;
  border-radius: 8px;
  width: 560px;
`;

const SectionArrowIndicator = styled.div`
  margin-left: auto;
  display: flex;
  padding: 10px;
  border-radius: 50%;
  &:hover {
    background: #e8f7c6;
    ${({ even }) =>
      even &&
      `
      background: #FFEEC3;
    `}
  }
  &:active {
    background: #d9f39f;
    ${({ even }) =>
      even &&
      `
      background: #FFE39E;
    `}
  }
`;

const SectionItemTitleInner = styled.div`
  font-size: 32px;
  font-weight: 600;
  line-height: 38px;
  color: #343538;
  margin: 20px 0;
`;

const ConsultationButton = styled(Button)`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  color: #3c9041;
  padding: 20px 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-color: #3c9041;
  border-width: 2px;
  background: transparent;
  margin-top: 16px;
  width: fit-content;
  &:hover {
    background: #d3f1a7 !important;
  }
  &:active {
    background: #b3df72 !important;
  }
  ${({ even }) =>
    even &&
    `
    border-color: #c25100;
    border-width: 2px;
    color: #c25100;
    &:hover {
      background: #FFE1B0 !important;
      border-color: #c25100 !important;
      color: #c25100 !important;
    }
    &:active {
      background: #FDD18C !important;
      border-color: #c25100 !important;
      color: #c25100 !important;
    }
  `}
`;

const CornerIconWrapper = styled.div`
  position: absolulte;
  top: 0;
  right: 0;
`;

// --- Collapse Description Animation ---
const AnimateDescription = ({ isOpen, children, item }: { isOpen: boolean; children: React.ReactNode; item: any }) => (
  <AnimatePresence initial={false}>
    {isOpen && (
      <motion.div
        initial={{ height: 0 }}
        animate={{ height: 'auto' }}
        exit={{ height: 0 }}
        transition={{ duration: 0.1, ease: 'ease' }}
        style={{ overflow: 'hidden' }}
      >
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>{children}</div>
      </motion.div>
    )}
  </AnimatePresence>
);

interface Props {
  customHeader?: ReactElement;
}
const WhyChooseUsSection: FC<Props> = ({ customHeader }) => {
  const swiperRef: any = useRef(null);
  const lang = localStorage.getItem('lang') || 'vi';
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const loggedIn = !!localStorage.getItem('token');
  const [isOpenContactModal, setIsOpenContactModal] = useState(false);

  const data = [
    {
      id: 1,
      img: clarityProcess,
      descriptionEn:
        'Right after payment, businesses receive electronic VAT invoices automatically via our system. No requests, no waiting, just clean, traceable documentation for hassle-free financial reporting.',
      description:
        'Ngay sau thanh toán, doanh nghiệp sẽ nhận hóa đơn VAT điện tử tự động qua hệ thống. Không cần yêu cầu riêng, không lo thất lạc, Kamereo giúp việc kê khai tài chính trở nên nhanh gọn và minh bạch tuyệt đối.',
      title: 'Hóa đơn VAT rõ ràng,{break}kê khai dễ dàng',
      titleEn: 'Clear invoices,{break}effortless reporting',
      iconSvg: clarityInvoice,
      cornerIcon: (
        <CornerIconWrapper>
          <img
            src={slide1a}
            style={{
              width: '56px',
              position: 'absolute',
              right: '140px',
              top: '35px',
            }}
          />
          <img
            src={slide1b}
            style={{
              width: '100px',
              position: 'absolute',
              right: 0,
              top: '50px',
            }}
          />
          <img
            src={slide1c}
            style={{
              position: 'absolute',
              right: 0,
              top: '145px',
              height: '129px',
            }}
          />
        </CornerIconWrapper>
      ),
    },
    {
      id: 2,
      img: clearlyOriginal,
      descriptionEn:
        'Kamereo partners directly with VietGAP-certified (*) farms and implements a rigorous 4-layer quality control process from the farm, procurement center, and cold storage to final delivery. Every product is carefully inspected to ensure freshness, cleanliness, and consistency before reaching your kitchen.',
      description:
        'Kamereo hợp tác trực tiếp với các trang trại đạt chuẩn VietGAP và áp dụng quy trình kiểm soát chất lượng 4 lớp - từ nông trại, trung tâm thu mua, kho vận đến khâu giao hàng cuối cùng. Mỗi sản phẩm được kiểm tra kỹ lưỡng, đảm bảo chất lượng tươi mới, đạt chuẩn trước khi đến tay khách hàng.',
      title: 'Nguyên liệu đạt chuẩn,{break}bếp luôn sẵn sàng',
      titleEn: 'Certified ingredients,{break}kitchens always ready',
      iconSvg: certifiedIngredients,
      cornerIcon: (
        <CornerIconWrapper>
          <img src={slide2a} style={{ position: 'absolute', top: '50px', width: '70px', right: '100px' }} />
          <img src={slide2b} style={{ position: 'absolute', height: '125px', top: '50px', right: 0 }} />
        </CornerIconWrapper>
      ),
    },
    {
      id: 3,
      img: convenienceUsp,
      descriptionEn:
        'Kamereo brings a tech-driven solution to streamline procurement for F&B businesses. With a curated catalog of 3,000+ products, you can place one order on one platform, without juggling multiple suppliers. Manage everything from order approvals and branch-level oversight to spend analytics in one place. Save time, stay in control, and run your operations more efficiently than ever.',
      description:
        'Kamereo mang đến giải pháp công nghệ với nền tảng mua hàng tối ưu quy trình thu mua trong ngành F&B. Với danh mục sản phẩm hơn 3.000 loại doanh nghiệp có thể đặt toàn bộ nhu cầu chỉ trong một đơn hàng, một nền tảng - không cần quản lý nhiều nhà cung cấp, không tốn thời gian. Bạn có thể quản lý toàn bộ quy trình từ phê duyệt đơn, quản lý chi nhánh đến phân tích thu chi, giúp tiết kiệm thời gian và vận hành hiệu quả.',
      title: 'Tiện lợi thông minh,{break}quản lý dễ dàng',
      titleEn: 'Smart convenience,{break}seamless management',
      iconSvg: smartConvenience,
      cornerIcon: (
        <CornerIconWrapper>
          <img src={slide3a} style={{ height: '154px', position: 'absolute', right: 0 }} />
        </CornerIconWrapper>
      ),
    },
    {
      id: 4,
      img: quicklyDelivery,
      descriptionEn:
        'Kamereo accepts orders until midnight and guarantees delivery before 6 AM the next morning. With 99%+ on-time delivery, 98%+ full order completion, and 99.5%+ error-free products, your kitchen stays stocked and ready every day.',
      description:
        'Kamereo nhận đơn hàng đến 12h đêm và cam kết giao trước 6h sáng hôm sau. Với tỷ lệ 99%+ đơn giao đúng giờ, 98%+ giao đủ hàng và 99.5%+ không lỗi sản phẩm, Kamereo đảm bảo bếp luôn sẵn sàng khi bạn cần.',
      title: 'Đặt trễ vẫn kịp,{break}giao đúng - đủ - nhanh',
      titleEn: 'Always on time, fast,{break}and accurate',
      iconSvg: onTime,
      cornerIcon: (
        <CornerIconWrapper>
          <img src={slide4a} style={{ position: 'absolute', width: '118px', top: '30px', right: '0px' }} />
          <img src={slide4b} style={{ width: '120px', position: 'absolute', top: '30px', right: '120px' }} />
        </CornerIconWrapper>
      ),
    },
  ];

  return (
    <CommonSectionContainer removePaddingBottom={loggedIn}>
      <Container>
        {customHeader || (
          <SectionTitleBox>
            {lang === 'vi' ? (
              <>
                <SectionTitle>Vì sao nên chọn&nbsp;</SectionTitle>
                <SectionTitle highLight>mô hình “Từ nông trại đến doanh nghiệp"?</SectionTitle>
              </>
            ) : (
              <>
                <SectionTitle highLight>Why Choose Farm-Direct Sourcing&nbsp;</SectionTitle>
                <SectionTitle>with Kamereo?</SectionTitle>
              </>
            )}
          </SectionTitleBox>
        )}
        <BenefitsWrapper even={activeIndex % 2 === 0}>
          <SectionContainer>
            <SectionWrapper>
              {data.map((item, index) => {
                // const start = index * 0.2;
                // const end = start + 0.2;

                // const scrollValue = useTransform(scrollYProgress, [start, end], [0, 1]);
                // useEffect(() => {
                //   const unsubscribe = scrollValue.onChange((v) => {
                //     if (v >= 0.4 && activeIndex !== index) {
                //       setActiveIndex(index);
                //       swiperRef?.current?.slideTo(index);
                //     }
                //   });
                //   return () => unsubscribe();
                // }, [scrollValue, index, activeIndex]);

                // const isOpen = activeIndex === index;
                return (
                  <SectionItem
                    key={item.id}
                    even={index % 2 === 0}
                    onClick={() => {
                      setActiveIndex(index);
                      swiperRef?.current?.slideTo(index);
                    }}
                    active={activeIndex === index}
                    // style={{
                    //   opacity: useTransform(scrollYProgress, [start, end], [0, 1]),
                    //   y: useTransform(scrollYProgress, [start, end], [0, 0]),
                    // }}
                    notShowBorderBottom={index === activeIndex - 1}
                  >
                    <SectionItemTitle>
                      {activeIndex === index
                        ? ''
                        : lang === 'vi'
                          ? item.title.replace('{break}', ' ')
                          : item.titleEn.replace('{break}', ' ')}
                      {!(activeIndex === index) && (
                        <SectionArrowIndicator even={activeIndex % 2 === 0}>
                          <SVGIconImage src={dropdownIcon} />
                        </SectionArrowIndicator>
                      )}
                    </SectionItemTitle>
                    <AnimateDescription isOpen={activeIndex === index} item={item}>
                      {item.cornerIcon}
                      <div style={{ display: 'flex' }}>
                        <SVGIconImage src={item.iconSvg} />
                      </div>
                      <SectionItemTitleInner
                        dangerouslySetInnerHTML={{
                          __html:
                            lang === 'vi'
                              ? item.title.replace('{break}', '<br />')
                              : item.titleEn.replace('{break}', '<br />'),
                        }}
                      />
                      <SectionItemDescription>
                        {lang === 'vi' ? item.description : item.descriptionEn}
                      </SectionItemDescription>
                      {!loggedIn && (
                        <ConsultationButton
                          onClick={() => {
                            setIsOpenContactModal(true);
                            sendGA4Event(EventName.PRODUCT_CONSULT_FTB_CLICK);
                          }}
                          type="default"
                          even={index % 2 === 0}
                        >
                          <FormattedMessage {...messages.getProductConsultation} />
                          <SVGIcon
                            src={index % 2 === 0 ? arrowRightConsultationButton : arrowRightConsultationOddButton}
                          />
                        </ConsultationButton>
                      )}
                    </AnimateDescription>
                  </SectionItem>
                );
              })}
            </SectionWrapper>
            <SwiperElementContainer>
              <SwiperElement
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                slidesPerView={1}
                navigation={true}
                // modules={[Navigation]}
                className="mySwiper"
                loop={true}
                speed={0}
                allowTouchMove={false}
                // onActiveIndexChange={(swiper) => {
                //   setActiveIndex(swiper.activeIndex);
                // }}
              >
                {data.map((item, index) => {
                  return (
                    <SwiperSlideElement key={index}>
                      <SwiperSlideImageWrapper image={item.img} />
                    </SwiperSlideElement>
                  );
                })}
              </SwiperElement>
            </SwiperElementContainer>
          </SectionContainer>
        </BenefitsWrapper>
      </Container>
      <ContactUsModal isOpen={isOpenContactModal} onCloseContactModal={() => setIsOpenContactModal(false)} />
    </CommonSectionContainer>
  );
};

export default WhyChooseUsSection;
